/**
 * The company a session works in, from the browser's side (SB-321; ADR-0006, SB-318, SB-319).
 *
 * questions keeps the company on the session and the token carries it, so UDE decides who reaches
 * a project without calling questions. The browser never sees the token. What it does:
 *
 * - Switching company is `POST /api/auth/company`. questions checks the membership and sets a new
 *   cookie. Then the page loads again, as at sign-out, so nothing the old company loaded stays on
 *   screen or in memory.
 * - UDE answers a project in another of the person's companies with
 *   `409 {detail: {code: 'project_in_other_company', company_id}}`. A link into such a project
 *   switches when the project's page loads (`ProjectCompanyGate`), never on a background request.
 *   Every tab shares the one session, so a poll that switched would pull a second tab, open on
 *   another company's project, back and forth.
 * - Those 409s, and `no_company`, carry their detail as an object. The pages render `detail` as
 *   text, so the shared axios instance puts the Norwegian sentence there before any page sees it.
 */
import type {AxiosError, AxiosInstance} from 'axios';

import {noteTokenLifetime} from './session';

export type CompanySwitchOutcome = 'ok' | 'refused' | 'ended' | 'failed';

/** Ask questions for a session in another company: `refused` when the person may not work there. */
export async function requestCompanySwitch(companyId: string): Promise<CompanySwitchOutcome> {
    try {
        const response = await fetch('/api/auth/company', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({company_id: companyId}),
        });
        if (response.status === 401) return 'ended';
        if (response.status === 404) return 'refused';
        if (!response.ok) return 'failed';
        const body = await response.json().catch(() => null);
        noteTokenLifetime(body?.expires_in);
        return 'ok';
    } catch {
        return 'failed';
    }
}

/**
 * Where the app goes after a switch from the menu. A project belongs to one company, so a page
 * inside one would answer «not found» in the next; the list of the new company's projects stands
 * in for it. Every other page loads again where it is.
 */
export function pathAfterSwitch(pathname: string, search: string): string {
    if (/^\/ude\/projects\/(?!new(?:\/|$))[^/]+/.test(pathname)) return '/ude/projects';
    return pathname + search;
}

const SWITCHED_KEY = 'sb.companySwitchedForLink';
const LOOP_WINDOW_MS = 60_000;

/** The company UDE says a project is in, from the error of a request about it. */
export function projectCompanyOf(error: unknown): string | null {
    const response = (error as AxiosError<{code?: string; company_id?: string}>)?.response;
    if (response?.status !== 409 || response.data?.code !== 'project_in_other_company') return null;
    return typeof response.data.company_id === 'string' ? response.data.company_id : null;
}

/**
 * Switch to a project's company for a link into it. False when it may not, and false too when
 * this tab switched to that same company a moment ago and is still told to switch: then the page
 * shows its own error rather than loading again forever.
 */
export async function switchForLink(companyId: string): Promise<boolean> {
    let storage: Storage;
    try {
        storage = window.sessionStorage;
        const last = JSON.parse(storage.getItem(SWITCHED_KEY) ?? 'null');
        if (last?.company === companyId && Date.now() - last.at < LOOP_WINDOW_MS) return false;
    } catch {
        // Nowhere to note the switch, so a page still answered 409 after it would load forever.
        // The person switches from the user menu instead.
        return false;
    }
    if ((await requestCompanySwitch(companyId)) !== 'ok') return false;
    try {
        storage.setItem(SWITCHED_KEY, JSON.stringify({company: companyId, at: Date.now()}));
    } catch {
        return false;  // switched, but unnoted: the page goes on in the new company without a reload
    }
    return true;
}

/** What a page shows for UDE's coded 409s, in place of the detail object. */
const MESSAGES: Record<string, string> = {
    project_in_other_company:
        'Prosjektet ligger i et annet firma enn det du jobber i nå. Last siden på nytt for å bytte til det.',
    no_company: 'Du er ikke med i noe firma ennå. En administrator i firmaet ditt må legge deg til.',
};

/** The axios half: the coded 409s reach every page with their sentence as `detail`, and their
 * code and company beside it. */
export function installCompanyInterceptor(instance: AxiosInstance): void {
    instance.interceptors.response.use(undefined, async (error: AxiosError<{detail?: unknown}>) => {
        const response = error.response;
        const detail = response?.status === 409 ? response.data?.detail : undefined;
        if (response && detail && typeof detail === 'object') {
            const {code, company_id} = detail as {code?: string; company_id?: string};
            if (code && code in MESSAGES) {
                response.data = {...response.data, code, company_id, detail: MESSAGES[code]} as typeof response.data;
            }
        }
        throw error;
    });
}
